import React from "react";
import { EMAIL_ACCESS_RULES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function StaffRoles() {

  type PermissionAction = "view" | "create" | "update" | "delete";
  // Align keys with actual modules we manage
  type PermissionKey = "feed" | "category" | "subCategory" | "notifications";
  type Permissions = {
    [K in PermissionKey]: {
      [A in PermissionAction]: boolean;
    };
  };

  interface Role {
    roleName: string;
    roleId: number;
    permissions: Permissions;
  }

  // Build roles from configured email-based access along with Super Admin
  const superAdmin: Role = {
    roleName: "Super Admin",
    roleId: 1,
    permissions: {
      feed: { view: true, create: true, update: true, delete: true },
      category: { view: true, create: true, update: true, delete: true },
      subCategory: { view: true, create: true, update: true, delete: true },
      notifications: { view: true, create: true, update: true, delete: true },
    },
  };

  const emailDerivedRoles: Role[] = Object.entries(EMAIL_ACCESS_RULES).map(
    ([email, rule], idx) => {
      const isFeed = rule.allowedMenuIds.includes("feed");
      const isNotifications = rule.allowedMenuIds.includes("notifications");
      const label =
        email === "feeds@qjskills.com"
          ? "Feed Manager"
          : email === "notification@qjskills.com"
          ? "Notification Manager"
          : email;
      return {
        // Only show the role label; do not display the email in the table
        roleName: label,
        roleId: idx + 2, // ensure unique ids after Super Admin
        permissions: {
          feed: {
            view: isFeed,
            create: isFeed,
            update: isFeed,
            delete: false, // Remove delete access for Feed Manager
          },
          category: { view: false, create: false, update: false, delete: false },
          subCategory: {
            view: false,
            create: false,
            update: false,
            delete: false,
          },
          notifications: {
            view: isNotifications,
            create: isNotifications,
            update: isNotifications,
            delete: false, // Remove delete access for Notification Manager
          },
        },
      };
    }
  );

  const [roles, setRoles] = React.useState<Role[]>([
    superAdmin,
    ...emailDerivedRoles,
  ]);

  const [showForm, setShowForm] = React.useState(false);
  const [formData, setFormData] = React.useState<{
    roleName: string;
    roleId: string;
    permissions: Permissions;
  }>(
    {
      roleName: "",
      roleId: "",
      permissions: {
        feed: { view: false, create: false, update: false, delete: false },
        category: { view: false, create: false, update: false, delete: false },
        subCategory: { view: false, create: false, update: false, delete: false },
        notifications: { view: false, create: false, update: false, delete: false },
      },
    }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePermissionChange = (
    category: PermissionKey,
    action: PermissionAction
  ) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [category]: {
          ...prev.permissions[category],
          [action]: !prev.permissions[category][action],
        },
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.roleName || !formData.roleId) return;
    setRoles([
      ...roles,
      {
        roleName: formData.roleName,
        roleId: Number(formData.roleId),
        permissions: formData.permissions,
      },
    ]);
    setFormData({
      roleName: "",
      roleId: "",
      permissions: {
        feed: { view: false, create: false, update: false, delete: false },
        category: { view: false, create: false, update: false, delete: false },
        subCategory: { view: false, create: false, update: false, delete: false },
        notifications: { view: false, create: false, update: false, delete: false },
      },
    });
    setShowForm(false);
  };

  const permissionColumns: { key: PermissionKey; label: string }[] = [
    { key: "feed", label: "Feed" },
    { key: "category", label: "Category" },
    { key: "subCategory", label: "Sub-Category" },
    { key: "notifications", label: "Notifications" },
  ];
  const actions: PermissionAction[] = ["view", "create", "update", "delete"];

  return (
    <div className="space-y-4 relative">
      <div className="flex items-center gap-4">
        <h1 className="text-2xl">Staff Roles</h1>
        <Button onClick={() => setShowForm(true)}>
          Create Role
        </Button>
      </div>
      <p>Manage staff roles, permissions, and assignments here.</p>
      <div className="overflow-auto border rounded-lg">
        <Table className="text-base">
          <TableHeader>
            <TableRow>
              <TableHead>Role Name</TableHead>
              <TableHead>Role ID</TableHead>
              {permissionColumns.map((col) =>
                actions.map((action) => (
                  <TableHead key={col.key + action} className="text-center">
                    {col.label} {action.charAt(0).toUpperCase() + action.slice(1)}
                  </TableHead>
                ))
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.roleId}>
                <TableCell className="font-medium">{role.roleName}</TableCell>
                <TableCell>{role.roleId}</TableCell>
                {permissionColumns.map((col) =>
                  actions.map((action) => (
                    <TableCell key={col.key + action} className="text-center">
                      {role.permissions[col.key][action] ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#22c55e" className="mx-auto" style={{ width: 20, height: 20 }}>
                          <path fillRule="evenodd" d="M16.704 6.29a1 1 0 0 1 0 1.414l-6.25 6.25a1 1 0 0 1-1.414 0l-3.25-3.25a1 1 0 1 1 1.414-1.414l2.543 2.543 5.543-5.543a1 1 0 0 1 1.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '0.8em' }}>❌</span>
                      )}
                    </TableCell>
                  ))
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <form className="w-full max-w-md mx-auto bg-white p-8 rounded-2xl shadow-2xl space-y-6" onSubmit={handleSubmit}>
            <h2 className="text-xl font-semibold mb-2 text-gray-800">Create New Role</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Role Name</label>
                <input
                  type="text"
                  name="roleName"
                  value={formData.roleName}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Role ID</label>
                <input
                  type="number"
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Permissions</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {permissionColumns.map((col) => (
                  <div key={col.key} className="border rounded-lg p-3 bg-gray-50">
                    <div className="font-semibold mb-2 text-gray-700">{col.label}</div>
                    <div className="flex gap-3 flex-wrap">
                      {actions.map((action) => (
                        <label key={action} className="flex items-center gap-2 text-sm font-medium text-gray-600">
                          <input
                            type="checkbox"
                            checked={formData.permissions[col.key][action]}
                            onChange={() => handlePermissionChange(col.key, action)}
                            className="accent-blue-500 w-4 h-4 rounded focus:ring-2 focus:ring-blue-500"
                          />
                          {action.charAt(0).toUpperCase() + action.slice(1)}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="submit" disabled={formData.roleName === "" || formData.roleId === ""} className="px-6 py-2 rounded-lg font-semibold">
                Save Role
              </Button>
              <Button type="button" variant="destructive" onClick={() => setShowForm(false)} className="px-6 py-2 rounded-lg font-semibold">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default StaffRoles;
